// Email editor screen — primary chapter-staff workflow. Per
// spec/build-plan.md P-032 row + tech-arch.md §6 + §7 + §8 + §2.5.
// Form-on-left + preview-on-right at md+; two horizontal tabs at <md
// per Banks's pre-flight call (Ask 1). First reachable mount of
// `<PreviewIframe>` (D-091) + `<PreviewToolbar>` (D-092) + active
// Tiptap rendering surface (D-090).
//
// Param-id validation mirrors `<TemplateNotFound>` (D-093). Toggle
// state (viewport) and the mobile-tab selection live as component
// state (D-092 reasoning — UI signals don't earn an AppState
// schemaVersion bump). Save is implicit via D-083 auto-persist.
// Color-scheme state was removed at P-037 close per D-100 alongside
// the dark-mode shell removal.
//
// V1 scope (Banks-confirmed at pre-flight Asks 3 + 4): Block reorder
// is NOT surfaced (order inherited from Template); per-Block options
// form is NOT mounted (options snapshot from Template at create time
// per Tech Arch §2.5). Editor mounts forms only over `inputsSchema`.
//
// Mobile tabs use `hidden` (CSS-driven) so the inactive pane stays
// mounted — no Tiptap remount, no preview iframe reload across tab
// switches.
//
// **Two-pane sizing (P-037.26 D-134).** The preview pane is a fixed
// 720 px column (680 px iframe per `tokens.layout.containerWidth` +
// 40 px breathing room for the `bg-gray-100 p-2` outer padding +
// scrollbar gutter); the form pane takes the remaining 1fr inside
// the wide PageContainer (max-w-screen-xl, ~512 px form pane).
// Replaces the prior 50/50 split where each pane got ~488 px and the
// preview pane's overflow-hidden wrapper visibly clipped the 680 px
// iframe past its right edge.
//
// **`autoHeight` on the preview iframe (P-037.26 D-134).** Mirrors
// the Block Gallery's behavior (D-115) — the iframe grows to its
// rendered `body.scrollHeight` so taller Blocks (`big3-signature-
// events`, footer, kitchen-sink composition) render in full without
// being clipped at the iframe's fixed-height fold. The outer
// `flex-1 overflow-auto` wrapper scrolls naturally when the email
// exceeds the viewport-clamped sticky container.
//
// **Block-manipulation controls reach parity with the Template editor
// (P-037.30 / D-138 Item 1).** Up/down/remove buttons render in each
// BlockSection header; an "+ Add block" CTA opens the same
// `<BlockPickerModal>` the Template editor uses. The original P-032
// V1 scope deferred Block reorder/add to keep the Email a pure
// content-editor; Banks's audit reversed that decision so chapter
// staff can customize an Email beyond the Template snapshot without
// editing the source Template. Mutations are Email-local per Tech
// Arch §2.5 (no Template propagation; consistent with the existing
// `included` toggle's local-divergence pattern). Fresh `instanceId`s
// via `newId()` keep new instances collision-safe alongside the
// Template-sourced ids already in the array.
//
// **Pencil-affordance discoverability fix (P-037.30 / D-138 Item 2).**
// The D-135 inline-edit title was too subtle — at rest, the idle
// button looked like static text. The idle state now renders a
// pencil glyph next to the title + an italicized CTA placeholder
// ("Click to name this email…") when the name is empty. Edit-mode
// JSX is unchanged.

import { useEffect, useRef, useState } from 'preact/hooks'

import { blocks, type BlockId } from '../../registry/blocks'
import { duplicateAndOpenEmail, updateEmail } from '../../store/factories'
import { secondaryButtonClass } from '../button-classes'
import type { Email } from '../../store/schemas'
import { store } from '../../store/store'
import { ConfirmModal } from '../confirm-modal'
import { EMAIL_NAME_EDIT_PROMPT, EMAIL_NAME_PLACEHOLDER } from '../constants'
import { useRoute } from '../hooks/use-route'
import { useStore } from '../hooks/use-store'
import { PreviewIframe } from '../preview/preview-iframe'
import { PreviewToolbar } from '../preview/preview-toolbar'
import type { Viewport } from '../../types'
import { BlockPickerModal } from './block-picker-modal'
import { BlockSection } from './block-section'
import { DirtyAsterisk } from './dirty-asterisk'
import { EmailEditorMobileTabs, type EmailEditorMobileTab } from './email-editor-tabs'
import {
  addBlockToEmail,
  moveBlockInEmail,
  removeBlockFromEmail,
  setBlockIncluded,
  setBlockInputs,
  setEmailName,
  setEmailPreviewText,
  setEmailSubjectLine,
} from './email-editor-helpers'
import { OutputToolbar } from './email-editor-output'
import { SavedIndicator } from './saved-indicator'

function deleteEmailAndLeave(id: string) {
  store.setState((s) => ({
    ...s,
    emails: s.emails.filter((e) => e.id !== id),
    currentEmailId: s.currentEmailId === id ? null : s.currentEmailId,
  }))
  window.location.hash = '#/emails'
}

function EmailNotFound({ id }: { id: string }) {
  return (
    <div class="space-y-4">
      <h2 class="m-0 text-lg font-semibold">Email not found</h2>
      <p class="m-0 text-sm text-gray-600">
        No Email with id <code class="font-mono">{id}</code>.
      </p>
      <a href="#/emails" class={`inline-block ${secondaryButtonClass}`}>
        ← Back to Emails
      </a>
    </div>
  )
}

export function EmailEditorScreen() {
  const { params } = useRoute()
  const { emails } = useStore()
  const id = params.id ?? ''
  const email = emails.find((e) => e.id === id)
  const [viewport, setViewport] = useState<Viewport>('desktop')
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [mobileTab, setMobileTab] = useState<EmailEditorMobileTab>('edit')
  const [editingName, setEditingName] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const nameInputRef = useRef<HTMLInputElement | null>(null)

  // Focus + select-all when entering edit mode so staff can immediately type
  // over the existing name (Notion / Linear idiom).
  useEffect(() => {
    if (editingName) {
      nameInputRef.current?.focus()
      nameInputRef.current?.select()
    }
  }, [editingName])

  if (email === undefined) return <EmailNotFound id={id} />

  const dispatch = (transform: (e: Email) => Email) => {
    updateEmail(email.id, transform)
  }

  const editPaneClass = mobileTab === 'edit' ? 'block md:block' : 'hidden md:block'
  const previewPaneClass = mobileTab === 'preview' ? 'block md:block' : 'hidden md:block'

  return (
    <div class="space-y-4">
      <header class="flex items-baseline justify-between gap-4">
        <div class="min-w-0">
          <h2 class="m-0 flex items-baseline gap-2 truncate text-lg font-semibold">
            {editingName ? (
              <input
                ref={nameInputRef}
                type="text"
                value={email.name}
                onInput={(e) =>
                  dispatch((eml) => setEmailName(eml, (e.target as HTMLInputElement).value))
                }
                onBlur={() => setEditingName(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === 'Escape') {
                    ;(e.target as HTMLInputElement).blur()
                  }
                }}
                aria-label="Email name"
                placeholder={EMAIL_NAME_PLACEHOLDER}
                class="min-w-0 flex-1 truncate rounded border border-ypo-navy/40 bg-white px-1 text-lg font-semibold text-inherit focus:outline-none focus:ring-2 focus:ring-ypo-navy/30"
              />
            ) : (
              <button
                type="button"
                onClick={() => setEditingName(true)}
                aria-label="Rename email"
                class="-mx-1 inline-flex min-w-0 cursor-text items-baseline gap-1.5 truncate rounded px-1 text-left text-inherit hover:bg-gray-100"
              >
                <span
                  class={`min-w-0 truncate ${email.name === '' ? 'italic text-gray-500' : ''}`}
                >
                  {email.name || EMAIL_NAME_EDIT_PROMPT}
                </span>
                <span aria-hidden="true" class="shrink-0 text-base text-gray-400">
                  ✏
                </span>
              </button>
            )}
            <DirtyAsterisk />
          </h2>
          <div class="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs text-gray-500">
            <span>
              {email.templateName !== '' ? email.templateName : 'No template'} ·{' '}
              {email.blocks.length} block{email.blocks.length === 1 ? '' : 's'}
            </span>
            <SavedIndicator />
          </div>
        </div>
        <div class="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => duplicateAndOpenEmail(email)}
            class={secondaryButtonClass}
          >
            Duplicate
          </button>
          <button
            type="button"
            onClick={() => setDeleteOpen(true)}
            class="rounded border border-red-300 bg-white px-3 py-1.5 text-sm text-red-700 hover:bg-red-50"
          >
            Delete
          </button>
        </div>
      </header>

      <OutputToolbar email={email} />

      <EmailEditorMobileTabs active={mobileTab} onChange={setMobileTab} />

      <div class="grid gap-4 md:grid-cols-[minmax(0,1fr)_720px]">
        <section aria-label="Edit pane" class={`space-y-3 ${editPaneClass}`}>
          <EmailMetaFields email={email} dispatch={dispatch} />
          {email.blocks.length === 0 ? (
            <div class="rounded border border-dashed border-gray-300 bg-gray-50 p-6 text-center">
              <p class="m-0 text-sm text-gray-600">This Email has no Blocks yet.</p>
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                class="mt-3 rounded bg-ypo-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-ypo-navy-deep"
              >
                Add your first Block
              </button>
            </div>
          ) : (
            <>
              <ul class="m-0 list-none space-y-3 p-0">
                {email.blocks.map((instance, idx) => {
                  const def = blocks[instance.blockId as BlockId]
                  if (def === undefined) {
                    return (
                      <li
                        key={instance.instanceId}
                        class="rounded border border-dashed border-amber-300 bg-amber-50 p-3 text-sm text-amber-800"
                      >
                        Unknown block id <code class="font-mono">{instance.blockId}</code>.
                      </li>
                    )
                  }
                  return (
                    <BlockSection
                      key={instance.instanceId}
                      instance={instance}
                      def={def}
                      canMoveUp={idx > 0}
                      canMoveDown={idx < email.blocks.length - 1}
                      onMoveUp={() =>
                        dispatch((e) => moveBlockInEmail(e, instance.instanceId, -1))
                      }
                      onMoveDown={() =>
                        dispatch((e) => moveBlockInEmail(e, instance.instanceId, +1))
                      }
                      onRemove={() =>
                        dispatch((e) => removeBlockFromEmail(e, instance.instanceId))
                      }
                      onIncludedChange={(included) =>
                        dispatch((e) => setBlockIncluded(e, instance.instanceId, included))
                      }
                      onInputsChange={(next) =>
                        dispatch((e) => setBlockInputs(e, instance.instanceId, next))
                      }
                    />
                  )
                })}
              </ul>
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                class="rounded bg-ypo-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-ypo-navy-deep"
              >
                + Add block
              </button>
            </>
          )}
        </section>
        <section aria-label="Preview pane" class={previewPaneClass}>
          <div class="md:sticky md:top-4 md:flex md:max-h-[calc(100vh-2rem)] md:flex-col">
            <PreviewToolbar viewport={viewport} onViewportChange={setViewport} />
            <div class="flex-1 overflow-auto bg-gray-100 p-2">
              <PreviewIframe
                email={email}
                viewport={viewport}
                className="mx-auto min-h-[600px]"
                autoHeight
              />
            </div>
          </div>
        </section>
      </div>

      <BlockPickerModal
        open={pickerOpen}
        onPick={(blockId) => {
          dispatch((e) => addBlockToEmail(e, blockId))
          setPickerOpen(false)
        }}
        onCancel={() => setPickerOpen(false)}
      />

      <ConfirmModal
        open={deleteOpen}
        title="Delete this Email?"
        message={`"${email.name || EMAIL_NAME_PLACEHOLDER}" will be removed. This cannot be undone.`}
        onConfirm={() => {
          setDeleteOpen(false)
          deleteEmailAndLeave(email.id)
        }}
        onCancel={() => setDeleteOpen(false)}
      />
    </div>
  )
}

/**
 * Subject line + Preview text inputs for V2.2 Push (P-047 / D-142).
 * Hand-rolled rather than driven through the schema-walker form
 * generator: the two fields belong to the Email envelope, not to a
 * Block, so they have no entry point in the per-Block form pipeline.
 * Mirrors the hand-rolled Email name input above (also Email-envelope-
 * level).
 *
 * Subject + preview never reach the rendered HTML body — they go to
 * CC's API directly at P-048's Push call. Empty subject is allowed at
 * the schema layer per D-142 (matches D-135 `name` relaxation); the
 * Push button gates on non-empty at P-048.
 */
function EmailMetaFields({
  email,
  dispatch,
}: {
  email: Email
  dispatch: (transform: (e: Email) => Email) => void
}) {
  const subjectInputClass =
    'block w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-ypo-navy focus:outline-none focus:ring-1 focus:ring-ypo-navy'
  return (
    <fieldset class="m-0 space-y-3 rounded border border-gray-200 p-3">
      <legend class="px-1 text-xs font-medium uppercase tracking-wide text-gray-500">
        Email metadata
      </legend>
      <label class="block">
        <span class="mb-1 block text-sm font-medium text-gray-800">Subject line</span>
        <input
          type="text"
          name="subjectLine"
          value={email.subjectLine}
          onInput={(e) =>
            dispatch((eml) =>
              setEmailSubjectLine(eml, (e.target as HTMLInputElement).value),
            )
          }
          placeholder="What recipients see in their inbox subject column"
          class={subjectInputClass}
        />
      </label>
      <label class="block">
        <span class="mb-1 block text-sm font-medium text-gray-800">
          Preview text <span class="font-normal text-gray-500">(optional)</span>
        </span>
        <input
          type="text"
          name="previewText"
          value={email.previewText ?? ''}
          onInput={(e) =>
            dispatch((eml) =>
              setEmailPreviewText(eml, (e.target as HTMLInputElement).value),
            )
          }
          placeholder="Short teaser shown next to the subject (preheader)"
          class={subjectInputClass}
        />
      </label>
    </fieldset>
  )
}
